import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import {
  ledgerApi,
  type Site,
  type BalanceRow,
  type ExplainLine,
  type LedgerTxDetail,
} from '../services/ledger';
import { settingsApi, type ValueListItem } from '../services/settings';
import { productsApi } from '../services/products';
import { TableWrapper, TableHead, FilterRow } from '../components/TableWrapper';
import { formatBatchLabel, formatContainerDisplayId } from '../lib/batch';
import { formatQty } from '../lib/quantity';

function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}

function txTypeBadgeClass(txType: string): string {
  switch (txType) {
    case 'RECEIVE':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'CONSUME':
    case 'SHIP':
    case 'DISPOSE':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'PRODUCE':
      return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'MOVE':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'ADJUST':
    case 'INVENTORY_APPLY':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function describeSource(line: ExplainLine): string {
  if (line.sourceTitle) {
    return line.sourceTitle;
  }
  switch (line.refType) {
    case 'GOODS_RECEIPT':
      return line.txType === 'RECEIVE' ? 'Inköp' : 'Mottagning';
    case 'EXTRACTION':
      return 'Slungning';
    case 'BOTTLING':
      return 'Tappning';
    case 'BLEND':
      return 'Blandning';
    case 'FLAVOR_MIX':
      return 'Smaksättning';
    case 'SHIPMENT':
      return 'Kundleverans';
    case 'POLLEN_HARVEST':
      return 'Pollenskörd';
    case 'POLLEN_PACKING':
      return 'Pollentappning';
    case 'MANUAL':
      return 'Manuell händelse';
    default:
      return line.refType ?? '-';
  }
}

function summarizeSourceRow(row: Record<string, string | number | null>): string {
  const hasContainerId = row.containerId !== null && row.containerId !== undefined && row.containerId !== '';
  const bits = Object.entries(row)
    .filter(([key, value]) => {
      if (key === 'containerCode' && hasContainerId) {
        return false;
      }
      return value !== null && value !== '';
    })
    .map(([key, value]) => {
      const label = key === 'productName'
        ? 'Produkt'
        : key === 'containerId' || key === 'containerCode'
          ? 'Kärl'
          : key === 'batchCode'
            ? 'Batch'
            : key === 'siteName'
              ? 'Plats'
              : key === 'qty'
                ? 'Mängd'
                : key === 'uomLabel'
                  ? 'Enhet'
                  : key === 'unitWeightG'
                    ? 'g/st'
                    : key === 'totalKg'
                      ? 'Totalt kg'
                      : key;
      const formattedValue = key === 'containerId'
        ? String(value)
        : (key === 'containerCode' || key === 'batchCode')
          ? formatBatchLabel(String(value))
          : value;
      return `${label}: ${formattedValue}`;
  });
  return bits.join(' • ');
}

function compareBatchLabels(a: BalanceRow, b: BalanceRow): number {
  const aSite = (a.siteName ?? '').localeCompare(b.siteName ?? '', 'sv-SE');
  if (aSite !== 0) {
    return aSite;
  }

  const aLabel = a.containerId && a.containerId > 0 ? String(a.containerId) : formatBatchLabel(a.batchCode);
  const bLabel = b.containerId && b.containerId > 0 ? String(b.containerId) : formatBatchLabel(b.batchCode);
  const aNumber = Number(aLabel);
  const bNumber = Number(bLabel);
  const aIsNumber = aLabel !== '' && Number.isFinite(aNumber) && /^[0-9]+$/.test(aLabel);
  const bIsNumber = bLabel !== '' && Number.isFinite(bNumber) && /^[0-9]+$/.test(bLabel);

  if (aIsNumber && bIsNumber) {
    const diff = aNumber - bNumber;
    if (diff !== 0) {
      return diff;
    }
  } else if (aIsNumber !== bIsNumber) {
    return aIsNumber ? -1 : 1;
  } else {
    const labelDiff = aLabel.localeCompare(bLabel, 'sv-SE', { numeric: true, sensitivity: 'base' });
    if (labelDiff !== 0) {
      return labelDiff;
    }
  }

  return (a.productName ?? '').localeCompare(b.productName ?? '', 'sv-SE', { numeric: true, sensitivity: 'base' });
}

export function LedgerPage() {
  const [sites, setSites] = useState<Site[]>([]);
  const [categories, setCategories] = useState<ValueListItem[]>([]);
  const [containerStatuses, setContainerStatuses] = useState<ValueListItem[]>([]);
  const [balance, setBalance] = useState<BalanceRow[]>([]);
  const [siteId, setSiteId] = useState<number | ''>('');
  const [categoryItemId, setCategoryItemId] = useState<number | ''>('');
  const [statusItemId, setStatusItemId] = useState<number | ''>('');
  const [kind, setKind] = useState<'HONEY' | 'POLLEN' | ''>('');
  const [isActiveFilter, setIsActiveFilter] = useState<'1' | '0' | ''>('1');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [explainRow, setExplainRow] = useState<BalanceRow | null>(null);
  const [explainLines, setExplainLines] = useState<ExplainLine[]>([]);
  const [loadingExplain, setLoadingExplain] = useState(false);
  const [detail, setDetail] = useState<LedgerTxDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showDispose, setShowDispose] = useState(false);
  const [showZeroNegative, setShowZeroNegative] = useState(false);
  const toast = useToast();
  const { canRead, canWrite } = useAuth();
  const writable = canWrite('ledger');

  if (!canRead('ledger')) {
    return <Navigate to="/" replace />;
  }

  const loadSites = useCallback(async () => {
    try {
      const res = await ledgerApi.getSites();
      setSites(res.sites);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta platser');
    }
  }, [toast]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await settingsApi.getValueListItems('PRODUCT_CATEGORY');
      setCategories(res.items.filter((item) => !item.isHidden));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta produktkategorier');
    }
  }, [toast]);

  const loadContainerStatuses = useCallback(async () => {
    try {
      const [honeyRes, pollenRes] = await Promise.all([
        settingsApi.getValueListItems('HONEY_CONTAINER_STATUS'),
        settingsApi.getValueListItems('POLLEN_CONTAINER_STATUS'),
      ]);
      const merged = [...honeyRes.items, ...pollenRes.items]
        .filter((item) => !item.isHidden)
        .filter((item, index, all) => all.findIndex((candidate) => candidate.id === item.id) === index);
      setContainerStatuses(merged);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta batchstatusar');
    }
  }, [toast]);

  const loadBalance = useCallback(async () => {
    try {
      const query = filter.trim();
      const res = await ledgerApi.getBalance({
        siteId: typeof siteId === 'number' ? siteId : undefined,
        q: query !== '' ? query : undefined,
        categoryItemId: typeof categoryItemId === 'number' ? categoryItemId : undefined,
        statusItemId: typeof statusItemId === 'number' ? statusItemId : undefined,
        kind,
        onlyBatches: true,
        isActive: isActiveFilter,
      });
      setBalance(res.balance);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta saldo');
    } finally {
      setLoading(false);
    }
  }, [categoryItemId, filter, isActiveFilter, kind, siteId, statusItemId, toast]);

  useEffect(() => {
    void loadSites();
  }, [loadSites]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    void loadContainerStatuses();
  }, [loadContainerStatuses]);

  useEffect(() => {
    setLoading(true);
    void loadBalance();
  }, [loadBalance]);

  const handleExplain = async (row: BalanceRow) => {
    setExplainRow(row);
    setExplainLines([]);
    setDetail(null);
    setLoadingExplain(true);
    try {
      const res = await ledgerApi.getExplain({
        siteId: row.siteId,
        productId: row.productId,
        containerId: row.containerId,
        batchCode: row.batchCode,
        uomItemId: row.uomItemId,
        matchNoBatch: !row.containerId && !row.batchCode,
      });
      setExplainLines(res.lines);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta förklaring');
    } finally {
      setLoadingExplain(false);
    }
  };

  const handleOpenDetail = async (txId: number) => {
    setLoadingDetail(true);
    try {
      const res = await ledgerApi.getTransactionDetail(txId);
      setDetail(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte hämta detaljer');
    } finally {
      setLoadingDetail(false);
    }
  };

  const filtered = [...balance].sort(compareBatchLabels);

  if (loading && balance.length === 0) {
    return <p className="text-sm text-gray-500">Laddar…</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Batchöversikt</h1>
        <WithWrite moduleCode="ledger">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowMove(true)}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
            >
              Flytt
            </button>
            <button
              type="button"
              onClick={() => setShowAdjust(true)}
              className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
            >
              Justering
            </button>
          </div>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-sm">Plats</label>
        <select
          value={siteId}
          onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}
          className="w-48"
        >
          <option value="">Alla</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <label className="text-sm">Kategori</label>
        <select
          value={categoryItemId}
          onChange={(e) => setCategoryItemId(e.target.value ? Number(e.target.value) : '')}
          className="w-48"
        >
          <option value="">Alla</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>{category.label}</option>
          ))}
        </select>
        <label className="text-sm">Typ</label>
        <select
          value={kind}
          onChange={(e) => setKind((e.target.value as 'HONEY' | 'POLLEN' | '') || '')}
          className="w-36"
        >
          <option value="">Alla</option>
          <option value="HONEY">Honung</option>
          <option value="POLLEN">Pollen</option>
        </select>
        <label className="text-sm">Status</label>
        <select
          value={statusItemId}
          onChange={(e) => setStatusItemId(e.target.value ? Number(e.target.value) : '')}
          className="w-48"
        >
          <option value="">Alla</option>
          {containerStatuses.map((status) => (
            <option key={status.id} value={status.id}>{status.label}</option>
          ))}
        </select>
        <label className="text-sm">Aktiv</label>
        <select
          value={isActiveFilter}
          onChange={(e) => setIsActiveFilter((e.target.value as '1' | '0' | '') || '')}
          className="w-32"
        >
          <option value="">Alla</option>
          <option value="1">Aktiv</option>
          <option value="0">Stängd</option>
        </select>
        <input
          type="text"
          placeholder="Sök batch, produkt, plats"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-64"
        />
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="text-left px-2 py-1.5 font-medium">Batch</th>
            <th className="text-left px-2 py-1.5 font-medium">Produkt</th>
            <th className="text-left px-2 py-1.5 font-medium">Plats</th>
            <th className="text-left px-2 py-1.5 font-medium">Status</th>
            <th className="text-right px-2 py-1.5 font-medium">Vattenhalt</th>
            <th className="text-left px-2 py-1.5 font-medium">Nektar</th>
            <th className="text-right px-2 py-1.5 font-medium">Saldo</th>
          </tr>
          <FilterRow>
            <td colSpan={7} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filtered.map((b) => (
            <tr
              key={`${b.siteId}-${b.productId}-${b.containerId ?? 'n'}-${b.batchCode ?? ''}-${b.uomItemId ?? ''}`}
              className="cursor-pointer border-b border-gray-100 hover:bg-amber-50"
              onClick={() => handleExplain(b)}
            >
              <td className="px-2 py-1 font-medium">
                <div>{formatContainerDisplayId(b.containerId, b.batchCode) || '-'}</div>
                <div className="text-xs text-gray-500">
                  {b.kind === 'POLLEN' ? 'Pollenbatch' : 'Honungsbatch'}
                  {b.isActive === 0 ? ' • stängd' : ''}
                </div>
              </td>
              <td className="px-2 py-1">{b.productName}</td>
              <td className="px-2 py-1">{b.siteName ?? sites.find((s) => s.id === b.siteId)?.name ?? b.siteId}</td>
              <td className="px-2 py-1">{b.statusLabel ?? '-'}</td>
              <td className="px-2 py-1 text-right">
                {b.waterPercent !== null && b.waterPercent !== ''
                  ? `${Number(b.waterPercent).toFixed(1)}%`
                  : '-'}
              </td>
              <td className="px-2 py-1">{b.nectarLabel ?? '-'}</td>
              <td className="px-2 py-1 text-right">{formatQty(b.qtyOnHand, b.uomLabel)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrapper>

      {filtered.length === 0 && (
        <p className="text-sm text-gray-500">Inget saldo eller inga träffar.</p>
      )}

      {explainRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold">Förklara saldo</h2>
                  <div className="mt-1 text-sm text-gray-600">
                    {explainRow.productName}
                    {' • '}
                    {formatContainerDisplayId(explainRow.containerId, explainRow.batchCode) || 'Utan batch'}
                    {' • '}
                    {explainRow.siteName ?? sites.find((s) => s.id === explainRow.siteId)?.name ?? explainRow.siteId}
                    {' • '}
                    {formatQty(explainRow.qtyOnHand, explainRow.uomLabel)} {explainRow.uomLabel ?? ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setExplainRow(null);
                    setExplainLines([]);
                    setDetail(null);
                    setShowDispose(false);
                    setShowZeroNegative(false);
                  }}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
                >
                  Stäng
                </button>
              </div>
              {writable && Number(explainRow.qtyOnHand) > 0 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowDispose(true)}
                    className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                  >
                    Kassera restsaldo
                  </button>
                </div>
              )}
              {writable && Number(explainRow.qtyOnHand) < 0 && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowZeroNegative(true)}
                    className="rounded border border-amber-300 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-50"
                  >
                    Nolla minussaldo
                  </button>
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-auto p-4">
              {loadingExplain ? (
                <p className="text-sm text-gray-500">Hämtar förklaring…</p>
              ) : explainLines.length === 0 ? (
                <p className="text-sm text-gray-500">Ingen förklaring hittades för vald batch.</p>
              ) : (
                <TableWrapper>
                  <TableHead>
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Datum</th>
                      <th className="px-2 py-1.5 text-left font-medium">Händelse</th>
                      <th className="px-2 py-1.5 text-left font-medium">Källa</th>
                      <th className="px-2 py-1.5 text-left font-medium">Batch</th>
                      <th className="px-2 py-1.5 text-right font-medium">Mängd</th>
                      <th className="px-2 py-1.5 text-left font-medium">Skapad av</th>
                      <th className="px-2 py-1.5 text-left font-medium">Kommentar</th>
                    </tr>
                    <FilterRow>
                      <td colSpan={7} className="px-2 py-1" />
                    </FilterRow>
                  </TableHead>
                  <tbody>
                    {explainLines.map((line) => (
                      <tr
                        key={line.id}
                        className="cursor-pointer border-b border-gray-100 hover:bg-amber-50"
                        onClick={() => handleOpenDetail(line.txId)}
                      >
                        <td className="px-2 py-1.5 whitespace-nowrap">{formatDateTime(line.txDate)}</td>
                        <td className="px-2 py-1.5">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${txTypeBadgeClass(line.txType)}`}>
                            {line.txTypeLabel}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          {line.sourcePath ? (
                            <Link
                              to={line.sourcePath}
                              className="text-primary hover:underline"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {describeSource(line)}
                            </Link>
                          ) : (
                            describeSource(line)
                          )}
                        </td>
                        <td className="px-2 py-1.5">{formatContainerDisplayId(line.containerId, line.batchCode) || '-'}</td>
                        <td className="px-2 py-1.5 text-right">{formatQty(line.qty, line.uomLabel)}</td>
                        <td className="px-2 py-1.5">{line.createdByName ?? '-'}</td>
                        <td className="px-2 py-1.5 text-sm text-gray-600">{line.txNotes ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableWrapper>
              )}
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold">Förklaring</h2>
                  <div className="mt-1 text-sm text-gray-600">
                    {detail.tx.txTypeLabel} • {formatDateTime(detail.tx.txDate)}
                  </div>
                </div>
                <button type="button" onClick={() => setDetail(null)} className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                  Stäng
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto p-4">
              {loadingDetail ? (
                <p className="text-sm text-gray-500">Hämtar detaljer…</p>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 md:grid-cols-2">
                    <div><span className="font-medium">Händelse:</span> {detail.tx.txTypeLabel}</div>
                    <div><span className="font-medium">Datum:</span> {formatDateTime(detail.tx.txDate)}</div>
                    <div><span className="font-medium">Plats:</span> {detail.tx.siteName ?? '-'}</div>
                    <div><span className="font-medium">Skapad av:</span> {detail.tx.createdByName ?? '-'}</div>
                    <div className="md:col-span-2"><span className="font-medium">Kommentar:</span> {detail.tx.notes ?? '-'}</div>
                  </div>

                  {detail.source && (
                    <div className="rounded-xl border border-gray-200 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold">{detail.source.title}</h3>
                          {detail.source.subtitle ? <div className="text-sm text-gray-600">{detail.source.subtitle}</div> : null}
                        </div>
                        {detail.source.path && detail.source.pathLabel ? (
                          <Link to={detail.source.path} className="rounded border border-primary px-3 py-1.5 text-sm font-medium text-primary hover:bg-orange-50">
                            {detail.source.pathLabel}
                          </Link>
                        ) : null}
                      </div>

                      {detail.source.fields.length > 0 && (
                        <div className="mt-4 grid gap-3 md:grid-cols-2">
                          {detail.source.fields
                            .filter((field) => field.value)
                            .map((field) => (
                              <div key={`${field.label}-${field.value}`} className="text-sm">
                                <div className="text-gray-500">{field.label}</div>
                                <div>{field.value}</div>
                              </div>
                            ))}
                        </div>
                      )}

                      {detail.source.rows.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <div className="text-sm font-medium">Källdetaljer</div>
                          {detail.source.rows.map((row, index) => (
                            <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                              {summarizeSourceRow(row)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="rounded-xl border border-gray-200 p-4">
                    <div className="mb-3 font-semibold">Lagrader</div>
                    <TableWrapper>
                      <TableHead>
                        <tr>
                          <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
                          <th className="px-2 py-1.5 text-left font-medium">Kärl/Batch</th>
                          <th className="px-2 py-1.5 text-left font-medium">Plats</th>
                          <th className="px-2 py-1.5 text-right font-medium">Mängd</th>
                          <th className="px-2 py-1.5 text-left font-medium">Enhet</th>
                        </tr>
                        <FilterRow>
                          <td colSpan={5} className="px-2 py-1" />
                        </FilterRow>
                      </TableHead>
                      <tbody>
                        {detail.lines.map((line) => (
                          <tr key={line.id} className="border-b border-gray-100">
                            <td className="px-2 py-1.5">{line.productName}</td>
                            <td className="px-2 py-1.5">{formatContainerDisplayId(line.containerId, line.containerCode ?? line.batchCode) || '-'}</td>
                            <td className="px-2 py-1.5">{line.siteName ?? '-'}</td>
                            <td className="px-2 py-1.5 text-right">{formatQty(line.qty, line.uomLabel)}</td>
                            <td className="px-2 py-1.5">{line.uomLabel ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </TableWrapper>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showMove && (
        <MoveForm
          sites={sites}
          onClose={() => setShowMove(false)}
          onSaved={() => { setShowMove(false); void loadBalance(); toast.success('Flytt genomförd'); }}
          toast={toast}
        />
      )}

      {showAdjust && (
        <AdjustForm
          sites={sites}
          onClose={() => setShowAdjust(false)}
          onSaved={() => { setShowAdjust(false); void loadBalance(); toast.success('Justering sparad'); }}
          toast={toast}
        />
      )}

      {showDispose && explainRow && (
        <DisposeForm
          row={explainRow}
          onClose={() => setShowDispose(false)}
          onSaved={() => {
            setShowDispose(false);
            void loadBalance();
            void handleExplain(explainRow);
            toast.success('Kassation sparad');
          }}
          toast={toast}
        />
      )}

      {showZeroNegative && explainRow && (
        <ZeroNegativeBalanceForm
          row={explainRow}
          onClose={() => setShowZeroNegative(false)}
          onSaved={() => {
            setShowZeroNegative(false);
            void loadBalance();
            void handleExplain(explainRow);
            toast.success('Minussaldo nollat');
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

function MoveForm({
  sites,
  onClose,
  onSaved,
  toast,
}: {
  sites: Site[];
  onClose: () => void;
  onSaved: () => void;
  toast: { error: (m: string) => void };
}) {
  const [fromSiteId, setFromSiteId] = useState<number | ''>('');
  const [toSiteId, setToSiteId] = useState<number | ''>('');
  const [productId, setProductId] = useState<number | ''>('');
  const [qty, setQty] = useState('');
  const [products, setProducts] = useState<Array<{ id: number; name: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    productsApi.list().then((r) => setProducts(r.products.map((p) => ({ id: p.id, name: p.name })))).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromSiteId || !toSiteId || fromSiteId === toSiteId || !productId || !qty || Number(qty) <= 0) {
      toast.error('Välj från-plats, till-plats, produkt och mängd större än 0');
      return;
    }
    setSaving(true);
    try {
      await ledgerApi.createTransaction({
        txType: 'MOVE',
        lines: [{ productId, fromSiteId, toSiteId, qty: Number(qty) }],
      });
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded shadow-lg max-w-sm w-full p-4">
        <h3 className="font-semibold mb-2">Flytt</h3>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div>
            <label className="block text-sm">Från plats</label>
            <select value={fromSiteId} onChange={(e) => setFromSiteId(e.target.value ? Number(e.target.value) : '')} className="w-full" required>
              <option value="">-</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm">Till plats</label>
            <select value={toSiteId} onChange={(e) => setToSiteId(e.target.value ? Number(e.target.value) : '')} className="w-full" required>
              <option value="">-</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm">Produkt</label>
            <select value={productId} onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : '')} className="w-full" required>
              <option value="">-</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm">Mängd</label>
            <input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full" required />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className="px-2 py-1 bg-success text-white rounded text-sm disabled:opacity-50">Spara</button>
            <button type="button" onClick={onClose} className="px-2 py-1 border rounded text-sm">Avbryt</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdjustForm({
  sites,
  onClose,
  onSaved,
  toast,
}: {
  sites: Site[];
  onClose: () => void;
  onSaved: () => void;
  toast: { error: (m: string) => void };
}) {
  const [siteId, setSiteId] = useState<number | ''>('');
  const [productId, setProductId] = useState<number | ''>('');
  const [qty, setQty] = useState('');
  const [products, setProducts] = useState<Array<{ id: number; name: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    productsApi.list().then((r) => setProducts(r.products.map((p) => ({ id: p.id, name: p.name })))).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!siteId || !productId || !qty || Number(qty) === 0) {
      toast.error('Välj plats, produkt och mängd');
      return;
    }
    setSaving(true);
    try {
      await ledgerApi.createTransaction({
        txType: 'ADJUST',
        siteId,
        lines: [{ productId, siteId, qty: Number(qty) }],
      });
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded shadow-lg max-w-sm w-full p-4">
        <h3 className="font-semibold mb-2">Justering</h3>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div>
            <label className="block text-sm">Plats</label>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')} className="w-full" required>
              <option value="">-</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm">Produkt</label>
            <select value={productId} onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : '')} className="w-full" required>
              <option value="">-</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm">Mängd (+ tillägg, − avdrag)</label>
            <input type="number" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full" required />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} className="px-2 py-1 bg-success text-white rounded text-sm disabled:opacity-50">Spara</button>
            <button type="button" onClick={onClose} className="px-2 py-1 border rounded text-sm">Avbryt</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DisposeForm({
  row,
  onClose,
  onSaved,
  toast,
}: {
  row: BalanceRow;
  onClose: () => void;
  onSaved: () => void;
  toast: { error: (m: string) => void };
}) {
  const [qty, setQty] = useState(String(Number(row.qtyOnHand)));
  const [notes, setNotes] = useState(row.batchCode ? `Kassation av restsaldo ${row.batchCode}` : 'Kassation av restsaldo');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedQty = Number(qty);
    if (!Number.isFinite(parsedQty) || parsedQty <= 0) {
      toast.error('Ange en mängd större än 0');
      return;
    }
    if (parsedQty > Number(row.qtyOnHand)) {
      toast.error('Kan inte kassera mer än aktuellt saldo');
      return;
    }
    setSaving(true);
    try {
      await ledgerApi.createTransaction({
        txType: 'DISPOSE',
        siteId: row.siteId,
        notes,
        lines: [{
          productId: row.productId,
          siteId: row.siteId,
          qty: parsedQty,
          uomItemId: row.uomItemId,
          containerId: row.containerId,
          batchCode: row.containerId ? null : row.batchCode,
        }],
      });
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte spara kassation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded bg-white p-4 shadow-lg">
        <h3 className="mb-2 font-semibold">Kassera restsaldo</h3>
        <div className="mb-3 text-sm text-gray-600">
          {row.productName} • {row.batchCode ?? 'Utan batch'} • {formatQty(row.qtyOnHand, row.uomLabel)}
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm">Mängd att kassera</label>
            <input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full" required />
          </div>
          <div>
            <label className="block text-sm">Kommentar</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[80px] w-full" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              {saving ? 'Sparar...' : 'Kassera'}
            </button>
            <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-sm">
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ZeroNegativeBalanceForm({
  row,
  onClose,
  onSaved,
  toast,
}: {
  row: BalanceRow;
  onClose: () => void;
  onSaved: () => void;
  toast: { error: (m: string) => void };
}) {
  const adjustmentQty = Math.abs(Number(row.qtyOnHand));
  const [notes, setNotes] = useState(
    row.batchCode ? `Nollar minussaldo för ${row.batchCode}` : 'Nollar minussaldo'
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!Number.isFinite(adjustmentQty) || adjustmentQty <= 0) {
      toast.error('Det finns inget minussaldo att nolla');
      return;
    }
    setSaving(true);
    try {
      await ledgerApi.createTransaction({
        txType: 'ADJUST',
        siteId: row.siteId,
        notes,
        lines: [{
          productId: row.productId,
          siteId: row.siteId,
          qty: adjustmentQty,
          uomItemId: row.uomItemId,
          containerId: row.containerId,
          batchCode: row.containerId ? null : row.batchCode,
        }],
      });
      onSaved();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte nolla minussaldo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded bg-white p-4 shadow-lg">
        <h3 className="mb-2 font-semibold">Nolla minussaldo</h3>
        <div className="mb-3 text-sm text-gray-600">
          {row.productName} • {row.batchCode ?? 'Utan batch'} • {formatQty(row.qtyOnHand, row.uomLabel)}
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Justeringen kommer att skapa <span className="font-semibold">+{formatQty(String(adjustmentQty), row.uomLabel)}</span> för att nolla aktuell post.
          </div>
          <div>
            <label className="block text-sm">Kommentar</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="min-h-[80px] w-full" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="rounded bg-amber-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">
              {saving ? 'Sparar...' : 'Nolla saldo'}
            </button>
            <button type="button" onClick={onClose} className="rounded border px-3 py-1.5 text-sm">
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
